import { Controller } from "@hotwired/stimulus";
import { gsap } from "gsap";
import { whenStageActive } from "./utils/when_stage_active";

// Tweens the textContent of the element from `from`
// to `to` as an integer, when the closest audit stage
// becomes active. Used for score numbers,
// percentages, etc. that should feel like they're
// being counted up live.
//
// Honors prefers-reduced-motion: snaps to final
// value immediately when reduced.
//
// Why deferred play: the audit show page renders all
// stages up front (hidden by CSS). Without
// `whenStageActive`, the count-up would run while the
// stage is `display: none`, ending before the user
// sees it.
//
// Values:
// - from (Number, default 0)
// - to (Number, required)
// - duration (Number, default 0.8): seconds.
// - delay (Number, default 0): seconds before start.
// - ease (String, default "power3.out"): GSAP ease.
// - formatter (String, default ""): optional output
//   strategy. When set to "thousands", the integer
//   is rendered with locale thousand separators
//   (e.g. `4,600`). Default ("") keeps the original
//   plain-integer behavior.
//
// Re-animating on `to-value` change. The audit
// report's reading-menu canvas updates the score /
// loss / bucket numbers via dataset broadcasts AFTER
// page load (running -> painted). When `toValue`
// changes, replay the count-up from the currently
// displayed value (so it tweens 0 -> N rather than
// snapping to N). The initial connect() pass is
// skipped so we don't double-fire on mount.
export default class extends Controller {
  static values = {
    from: { type: Number, default: 0 },
    to: { type: Number, default: 0 },
    duration: { type: Number, default: 0.8 },
    delay: { type: Number, default: 0 },
    ease: { type: String, default: "power3.out" },
    formatter: { type: String, default: "" },
  };

  connect() {
    this.connected = true;
    this.currentValue = this.fromValue;
    this.element.textContent = this.format(
      this.currentValue
    );

    this.cancelWait = whenStageActive(
      this.element,
      () => this.play(this.fromValue, this.toValue)
    );
  }

  disconnect() {
    this.connected = false;
    this.cancelWait?.();
    this.tween?.kill();
  }

  toValueChanged(newValue, oldValue) {
    // Skip the initial value-set Stimulus fires
    // before connect(): that path is handled by
    // connect()'s whenStageActive call.
    if (oldValue === undefined) return;
    if (!this.connected) return;
    if (newValue === oldValue) return;

    // Cancel any in-flight tween and any pending
    // stage-active wait, then re-arm. Replay tweens
    // from the currently displayed value so the
    // new target reads as a continuation rather
    // than a snap.
    this.tween?.kill();
    this.cancelWait?.();
    const startFrom = this.currentValue;
    this.cancelWait = whenStageActive(
      this.element,
      () => this.play(startFrom, newValue)
    );
  }

  play(from, to) {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduced) {
      this.currentValue = to;
      this.element.textContent = this.format(to);
      return;
    }

    const counter = { v: from };
    this.currentValue = from;
    this.element.textContent = this.format(counter.v);
    this.tween = gsap.to(counter, {
      v: to,
      duration: this.durationValue,
      delay: this.delayValue,
      ease: this.easeValue,
      onUpdate: () => {
        this.currentValue = counter.v;
        this.element.textContent = this.format(
          counter.v
        );
      },
      onComplete: () => {
        this.currentValue = to;
        this.element.textContent = this.format(to);
      },
    });
  }

  format(value) {
    const rounded = Math.round(value);
    if (this.formatterValue === "thousands") {
      return rounded.toLocaleString("es-MX");
    }
    return String(rounded);
  }
}
